// Upload constraints shared by the backend (file-service validation) and the
// frontend (client-side pre-checks). Canonical home is here (root config.ts
// re-exports for backend consumers) because config.ts reads process.env at
// module scope and cannot be imported by browser code.

// 2 GB covers the design source we accept (PSD/AI) and social/delivery video.
// Uploads go direct to S3 via a presigned single-PUT (bytes never touch Lambda),
// so this is safe up to S3's 5 GB single-PUT ceiling; >5 GB would need multipart.
// The `file_size` column is bigint (not integer) so sizes above ~2.14 GB never overflow.
export const FILE_MAX_SIZE_BYTES = 2 * 1024 * 1024 * 1024 // 2 GB

// Multipart upload sizing. The threshold and part size govern the (not-yet-wired)
// transport selector in `UploaderService`; the batch size caps how many part
// presigns a single `POST /files/{id}/multipart` request may request at once.
export const FILE_MULTIPART_THRESHOLD_BYTES = 64 * 1024 * 1024 // 64 MB
export const MULTIPART_PART_SIZE_BYTES = 32 * 1024 * 1024 // 32 MB
export const MULTIPART_PRESIGN_BATCH_SIZE = 16

// Allowed upload content types.
//
// This list is intentionally restricted to the types whose bytes we can
// positively fingerprint on confirm (see `detectMimeType` in
// @atlas/core-utils/file and file-service `validateMagicBytes`). A declared
// type that cannot be verified is a spoofing risk, so we do not accept it —
// keep this list and the magic-byte detector in lockstep.
//
// Deliberately NOT accepted (no reliable content signature): legacy Office
// (.doc/.xls/.ppt), text/html, application/json, remaining design source
// (.indd/.xd/.fig/.sketch), non-MP3 audio, non-zip archives (gzip/tar/7z/rar),
// and the application/octet-stream catch-all.
//
// All four font formats are accepted, by two different routes. WOFF, WOFF2 and
// OTF each carry a fixed 4-byte ASCII tag and are matched on it. A bare .ttf has
// no tag to match — its sfnt version stamp `00 01 00 00` is a small integer, and
// matching that would admit any file starting on a low number — so it is
// identified structurally instead, by walking its table directory
// (`detectSfntTrueType`). ADR-0026 (amended twice) has the reasoning.
//
// .psd is accepted on its "8BPS" signature, and .ai as the PDF container it
// actually is: detection reports `application/pdf` and the two labels are treated
// as interchangeable — see `validateMimeType`'s own comment for the corpus
// measurement behind that, including why the Illustrator header markers were
// removed rather than tuned.
export const FILE_ALLOWED_CONTENT_TYPES = [
	// ─── Documents ───────────────────────────────────────────────────────────
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx (zip)
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx (zip)
	'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx (zip)
	'text/csv',
	'text/plain',
	// ─── Images ──────────────────────────────────────────────────────────────
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
	'image/tiff',
	'image/bmp',
	'image/svg+xml',
	'image/heic', // ISO-BMFF brand
	'image/avif', // ISO-BMFF brand
	'image/vnd.adobe.photoshop', // .psd — "8BPS" signature
	// ─── Design source ───────────────────────────────────────────────────────
	// .ai — a PDF container; the canonical type browsers report for the extension.
	'application/postscript',
	// ─── Video ────────────────────────────────────────────────────────────────
	'video/mp4', // ISO-BMFF brand
	'video/quicktime', // .mov (ISO-BMFF brand)
	'video/x-msvideo', // .avi (RIFF)
	'video/webm',
	'video/mpeg',
	// ─── Audio ───────────────────────────────────────────────────────────────
	'audio/mpeg', // .mp3 (ID3 tag or frame sync)
	// ─── Fonts ───────────────────────────────────────────────────────────────
	// RFC 8081 names. The legacy `application/font-woff` / `application/x-font-*`
	// spellings are aliases (see CONTENT_TYPE_ALIASES), never list members, so
	// exactly one canonical string per format is ever stored.
	'font/woff', // "wOFF" signature
	'font/woff2', // "wOF2" signature
	'font/otf', // "OTTO" sfnt version tag (CFF outlines)
	'font/ttf', // sfnt version 1.0 plus a valid table directory (TrueType outlines)
	// ─── Archives ──────────────────────────────────────────────────────────────
	'application/zip',
] as const

/**
 * Content types the worker can turn into a thumbnail.
 *
 * The single source of truth, consumed from BOTH ends: root `config.ts` spreads
 * it into the `thumbnail_generation` concern (which decides what reaches the
 * worker), and the frontend reads it to decide which files to request a thumbnail
 * for. It lives here rather than in `config.ts` for the reason at the top of this
 * file — `config.ts` reads `process.env` at module scope, so browser code cannot
 * import it.
 *
 * Both ends MUST derive from this list. A duplicated copy is how a type ends up
 * generating a thumbnail that the UI never asks for: the file is processed, the
 * thumbnail is written, and the tile shows a type marker forever — with no error
 * anywhere, because each side is internally consistent.
 *
 * Adding a type here is not sufficient on its own: the worker also needs a source
 * strategy for it (`thumbnail-sources/index.ts`), and a lockstep test fails if the
 * two drift.
 */
export const THUMBNAILABLE_CONTENT_TYPES = [
	// ─── Images ──────────────────────────────────────────────────────────────
	// Decoded natively by sharp/libvips, except where noted.
	'image/png',
	'image/jpeg',
	'image/webp',
	'image/gif',
	'image/tiff',
	'image/bmp', // via bmp-js — libvips has no BMP loader
	'image/svg+xml',
	'image/heic',
	'image/avif',
	'image/vnd.adobe.photoshop', // .psd — the file's own flattened composite
	// ─── PDF containers ──────────────────────────────────────────────────────
	// Page 1 rendered by a WASM rasterizer, with an embedded-preview fast path.
	'application/pdf',
	'application/postscript', // .ai
	// ─── Video ───────────────────────────────────────────────────────────────
	// A poster frame extracted with ffmpeg.
	'video/mp4',
	'video/quicktime',
	'video/x-msvideo',
	'video/webm',
	'video/mpeg',
] as const

/** True when the worker can generate a thumbnail for `contentType`. */
export function isThumbnailableContentType(contentType: string): boolean {
	return (THUMBNAILABLE_CONTENT_TYPES as readonly string[]).includes(contentType)
}

/**
 * Content types a BROWSER can render directly in an `<img>`.
 *
 * Deliberately narrower than the `image/*` prefix: PSD, TIFF and HEIC all carry
 * an `image/` type and none of them decode in a browser, so a `startsWith`
 * check produces an object URL that renders as a broken-image icon. It is also
 * a different question from what the SERVER can thumbnail — sharp handles PSD
 * and TIFF happily, which is why those thumbnails appear once processing
 * finishes. This list governs the LOCAL preview shown before upload.
 *
 * AVIF is included: every browser this app supports decodes it.
 */
export const BROWSER_RENDERABLE_CONTENT_TYPES = [
	'image/jpeg',
	'image/jpg',
	'image/png',
	'image/gif',
	'image/webp',
	'image/svg+xml',
	'image/avif',
] as const

/** True when a browser can render `contentType` in an `<img>` without conversion. */
export function isBrowserRenderable(contentType: string): boolean {
	return (BROWSER_RENDERABLE_CONTENT_TYPES as readonly string[]).includes(contentType)
}

/**
 * Extension → content type, for formats a browser may not recognise, and for a
 * source that names no type at all (Veeva Vault serves every file as
 * `application/octet-stream`).
 *
 * `File.type` is the BROWSER's guess from the extension, and browsers disagree:
 * Chrome maps `.psd` to `image/vnd.adobe.photoshop`, Safari reports an empty
 * string. Trusting it directly meant a PSD uploaded fine in one browser and was
 * rejected client-side in another — with no request and no console error to
 * show for it, since the allow-list check runs before anything is sent.
 *
 * Only formats the allow-list already accepts appear here. The server still
 * fingerprints the actual bytes on confirm (`assertMagicBytesMatch`), so this
 * widens what the client will ATTEMPT, never what is ultimately accepted: a
 * renamed file is still caught, just server-side where the bytes are.
 */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
	pdf: 'application/pdf',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	csv: 'text/csv',
	txt: 'text/plain',
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	gif: 'image/gif',
	psd: 'image/vnd.adobe.photoshop',
	ai: 'application/postscript',
	tif: 'image/tiff',
	tiff: 'image/tiff',
	heic: 'image/heic',
	avif: 'image/avif',
	webp: 'image/webp',
	svg: 'image/svg+xml',
	bmp: 'image/bmp',
	mp3: 'audio/mpeg',
	mp4: 'video/mp4',
	mov: 'video/quicktime',
	avi: 'video/x-msvideo',
	webm: 'video/webm',
	mpeg: 'video/mpeg',
	mpg: 'video/mpeg',
	zip: 'application/zip',
	// Fonts belong here more than any other format: browsers report them
	// inconsistently even when they report something, and several report nothing
	// at all. The extension only names the container, never the outline flavour
	// inside it, which is why `validateMimeType` treats `font/ttf` and `font/otf`
	// as interchangeable rather than trusting the answer given here.
	woff: 'font/woff',
	woff2: 'font/woff2',
	otf: 'font/otf',
	ttf: 'font/ttf',
}

/**
 * Non-canonical spellings browsers and operating systems use for formats that
 * have an official type, mapped to the one the allow-list carries.
 *
 * These are positive identifications, just not the registered name — so they
 * cannot be treated as "unknown" and resolved from the extension, and they
 * cannot be passed through either, since the allow-list would reject them.
 */
const CONTENT_TYPE_ALIASES: Record<string, string> = {
	'image/photoshop': 'image/vnd.adobe.photoshop',
	'image/x-photoshop': 'image/vnd.adobe.photoshop',
	'application/photoshop': 'image/vnd.adobe.photoshop',
	'application/x-photoshop': 'image/vnd.adobe.photoshop',
	'application/psd': 'image/vnd.adobe.photoshop',
	'image/psd': 'image/vnd.adobe.photoshop',
	// .ai — Illustrator's own type strings, plus what some browsers report from
	// the extension. Mapped to the canonical PostScript type the allow-list carries.
	'application/illustrator': 'application/postscript',
	'application/vnd.adobe.illustrator': 'application/postscript',
	'application/x-illustrator': 'application/postscript',
	'application/ai': 'application/postscript',
	'image/x-tiff': 'image/tiff',
	'image/x-ms-bmp': 'image/bmp',
	'audio/mp3': 'audio/mpeg',
	'audio/x-mpeg': 'audio/mpeg',
	'video/x-quicktime': 'video/quicktime',
	// Fonts: the pre-RFC 8081 and vendor spellings, all of which are still in
	// circulation. `font/*` was only registered in 2017, so the older names are
	// what an OS type database or an older browser build still reports.
	'application/font-woff': 'font/woff',
	'application/x-font-woff': 'font/woff',
	'application/font-woff2': 'font/woff2',
	'application/x-font-woff2': 'font/woff2',
	'font/opentype': 'font/otf',
	'application/x-font-otf': 'font/otf',
	'application/x-font-opentype': 'font/otf',
	'application/vnd.ms-opentype': 'font/otf',
	'font/truetype': 'font/ttf',
	'application/x-font-ttf': 'font/ttf',
	'application/x-font-truetype': 'font/ttf',
	// `application/font-sfnt` named the container rather than the flavour and was
	// deprecated by RFC 8081 without a successor for either half. It is resolved
	// to TrueType because that is what the files carrying it overwhelmingly are,
	// and because the two sfnt labels are interchangeable at confirm anyway.
	'application/font-sfnt': 'font/ttf',
}

/**
 * Extension → content type where the EXTENSION is the better authority, even
 * against a browser type that looks canonical.
 *
 * Only `.ai` qualifies. An Illustrator file is a PDF container, so a browser that
 * sniffs content reports `application/pdf` for one — a positive identification of
 * the container, and the wrong answer for the file. Both are thumbnailed the same
 * way, so nothing is lost there; what the override buys is a file labelled as the
 * artwork it is rather than the wrapper it happens to use — an `.ai` filed under
 * IMAGE and marked `AI`, not DOCUMENT and marked `PDF`.
 *
 * The cost, accepted deliberately: a genuine PDF *named* `.ai` is stored as
 * PostScript, so it is categorised IMAGE and leaves the `document` facet of the
 * `fileCategory` API filter. That is a self-inflicted naming case, and the
 * alternative — trusting the sniffed container — mislabels every real `.ai`.
 *
 * Narrow by design: this overrides a specific, plausible browser answer, so it is
 * justified only where the extension genuinely knows better.
 */
const EXTENSION_WINS_CONTENT_TYPES: Record<string, string> = {
	ai: 'application/postscript',
}

/**
 * The content type for a file the user picked: the browser's own value when it
 * has a useful, canonical one — else an alias mapping, else the extension.
 *
 * `application/octet-stream` counts as "not useful": it is what a browser
 * reports when it does not recognise a format, not a positive identification.
 */
export function resolveContentType(fileName: string, browserType: string): string {
	const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
	// Checked before the browser's value, unlike every other rule here — see
	// EXTENSION_WINS_CONTENT_TYPES for why `.ai` overrides a canonical-looking type.
	const extensionWins = EXTENSION_WINS_CONTENT_TYPES[extension]
	if (extensionWins) return extensionWins
	const aliased = CONTENT_TYPE_ALIASES[browserType]
	if (aliased) return aliased
	if (browserType && browserType !== 'application/octet-stream') return browserType
	return EXTENSION_CONTENT_TYPES[extension] ?? browserType
}
